import React, { useCallback, useContext, useEffect, useRef } from "react";
import "./ClickableHeatPoints.scss";
import { useFrame, useThree } from "@react-three/fiber";
import { useTranslation } from "react-i18next";
import { PortalContentContext } from "../../../../../context/PortalContentContext";
import { useOverlaySubject } from "../../../../../hooks/useOverlaySubject";
import { Vector3 } from "three";
import { TOverlaySubject } from "../../../../../context/OverlaySubjectContext";
import { PulseButton } from "../../../../PulseButton/PulseButton";
import { orderBy, unset } from "lodash";

interface IProps {
}

const positions = [
    new Vector3(12, .5, 8),
    new Vector3(8, 2.5, 4),
    new Vector3(0, 1, 0),
    new Vector3(10, 3, -10),
];

const size = [window.innerWidth, window.innerHeight];

export function ClickableHeatPoints(props: IProps) {
    const { camera } = useThree();
    const { t } = useTranslation();
    const { setContent: setPortalContent } = useContext(PortalContentContext);
    const { setSubject } = useOverlaySubject();
    const pointARef = useRef<HTMLDivElement>(null);
    const pointBRef = useRef<HTMLDivElement>(null);
    const pointCRef = useRef<HTMLDivElement>(null);
    const pointDRef = useRef<HTMLDivElement>(null);

    const handleMoveCameraPoints = useCallback(() => {
        const pointA = pointARef.current;
        const pointB = pointBRef.current;
        const pointC = pointCRef.current;
        const pointD = pointDRef.current;
        if (!pointA || !pointB || !pointC || !pointD) return;
        const points = [pointA, pointB, pointC, pointD];

        requestAnimationFrame(() => {

            const zPositions: {
                elm: HTMLDivElement,
                z: number
            }[] = [];
            positions.forEach((basePosition, index) => {
                const position = basePosition.clone();
                const elm = points[index];
                position.project(camera);

                const h = size[1];

                const x = position.x;
                const y = position.y;
                // const translateX = position.x * w * 0.5
                const translateXS = `calc((${x} * 100vw) * 0.5)`;
                const translateY = - y * h * 0.5
                // const translateYS = `calc((${position.y} * ${h}px) * 0.5)`;
                const scale = Math.max(0, (1 - position.z) * 100 * 1);
                let scaleS = `calc(min(4, max(0, (1 - ${position.z}) * 100 * 1)))`;
                if (document.body.classList.contains('orbit-moving')) {
                    scaleS = '0';
                }
                // elm.style.transform = `translateX(${translateX}px) translateY(${translateY}px) scale(${scale})`
                elm.style.transform = `translateX(${translateXS}) translateY(${translateY}px) scale(${scaleS})`
                // elm.style.transform = `translateX(${translateXS}) translateY(${translateYS})`
                zPositions.push({
                    elm,
                    z: position.z
                });
            });
            orderBy(zPositions, ['z'], ['desc']).forEach(({elm}, index) => {
                elm.style.zIndex = `${index + 1}`;
            })
        })



    }, [camera, pointARef, pointBRef, pointCRef, pointDRef]);

    useFrame(() => {
        handleMoveCameraPoints();
    });


    useEffect(() => {
        // Handle click on floating buttons
        const handleHeatButtonClick = (subject: TOverlaySubject) => {
            return () => {
                setSubject(subject);
            }
        }

        setPortalContent((prevState) => {
            const copy = {...prevState};
            copy['heatPoints'] = <React.Fragment>
                <div className="point point-a" ref={pointARef}>
                    <PulseButton className={"small filled"} onClick={handleHeatButtonClick('welcome')}>{t('scene.subjects.welcome.title')}</PulseButton>
                </div>
                <div className="point point-b" ref={pointBRef}>
                    <PulseButton className={"small filled"} onClick={handleHeatButtonClick('sales')}>{t('scene.subjects.sales.title')}</PulseButton>
                </div>
                <div className="point point-c" ref={pointCRef}>
                    <PulseButton className={"small filled"} onClick={handleHeatButtonClick('production')}>{t('scene.subjects.production.title')}</PulseButton>
                </div>
                <div className="point point-d" ref={pointDRef}>
                    <PulseButton className={"small filled"} onClick={handleHeatButtonClick('warehousing')}>{t('scene.subjects.warehousing.title')}</PulseButton>
                </div>
            </React.Fragment>;
            return copy;
        });
        return () => {
            setPortalContent((prevState) => {
                const copy = {...prevState};
                unset(copy, 'heatPoints');
                return copy;
            });
        }
    }, [setPortalContent, setSubject, t])

    return (
        <></>
    )
}
